import { useEffect, useRef, useState } from 'react';

const KEYBOARD_HEIGHT_THRESHOLD_PX = 120;

const isTextEntryElement = (el: Element | null): boolean => {
  if (!(el instanceof HTMLElement)) {
    return false;
  }

  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
};

const isLikelyMobileEnvironment = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia !== 'function') {
    return false;
  }

  // Prefer pointer capability over width-only checks for better tablet/desktop behavior.
  return window.matchMedia('(pointer: coarse)').matches;
};

export const useIsCoarsePointer = (): boolean => {
  const [isCoarsePointer, setIsCoarsePointer] = useState(isLikelyMobileEnvironment);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setIsCoarsePointer(false);
      return;
    }

    const mediaQuery = window.matchMedia('(pointer: coarse)');

    const update = () => {
      setIsCoarsePointer(mediaQuery.matches);
    };

    update();
    mediaQuery.addEventListener('change', update);

    return () => {
      mediaQuery.removeEventListener('change', update);
    };
  }, []);

  return isCoarsePointer;
};

export const useMobileKeyboardVisible = (): boolean => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const baselineViewportHeightRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const visualViewport = window.visualViewport;

    const getViewportHeight = () => visualViewport?.height ?? window.innerHeight;

    const evaluate = () => {
      if (!isLikelyMobileEnvironment()) {
        setIsKeyboardVisible(false);
        return;
      }

      const viewportHeight = getViewportHeight();
      if (viewportHeight > baselineViewportHeightRef.current) {
        baselineViewportHeightRef.current = viewportHeight;
      }

      const activeElement = document.activeElement;
      const isEditingFieldFocused = isTextEntryElement(activeElement);
      const heightDrop = baselineViewportHeightRef.current - viewportHeight;

      setIsKeyboardVisible(isEditingFieldFocused && heightDrop > KEYBOARD_HEIGHT_THRESHOLD_PX);
    };

    baselineViewportHeightRef.current = getViewportHeight();
    evaluate();

    const scheduleEvaluate = () => {
      window.requestAnimationFrame(evaluate);
    };

    window.addEventListener('resize', scheduleEvaluate);
    window.addEventListener('orientationchange', scheduleEvaluate);
    document.addEventListener('focusin', scheduleEvaluate);
    document.addEventListener('focusout', scheduleEvaluate);

    visualViewport?.addEventListener('resize', scheduleEvaluate);
    visualViewport?.addEventListener('scroll', scheduleEvaluate);

    return () => {
      window.removeEventListener('resize', scheduleEvaluate);
      window.removeEventListener('orientationchange', scheduleEvaluate);
      document.removeEventListener('focusin', scheduleEvaluate);
      document.removeEventListener('focusout', scheduleEvaluate);

      visualViewport?.removeEventListener('resize', scheduleEvaluate);
      visualViewport?.removeEventListener('scroll', scheduleEvaluate);
    };
  }, []);

  return isKeyboardVisible;
};
